import { useEffect, useRef } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { aiStore } from "../../stores/ai.store";
import { serverStore } from "../../stores/server.store";
import { userStore } from "../../stores/user.store";
import { can } from '@/utils/permissions';

// Live diagnosis indicator for a single server's "Uebersicht".
// - While a run is "investigating": shows an indeterminate progress bar.
// - When the run is done (diagnosed / no_issue / failed) and NOT yet patched:
//   shows a one-time "hint" (Neu badge) plus a link to the report.
// - Once the run is marked patched (fixed): disappears completely.
export const ServerDiagnosisProgress = observer(function ServerDiagnosisProgress() {
  const serverId = serverStore.activeServer?.id;
  const navigate = useNavigate();
  // Run IDs whose "done" hint the user has already seen/clicked (per mount).
  const seen = useRef<Set<string>>(new Set());

  // Dieselbe Regel wie das Backend (/api/ai/runs): ohne KI-Dashboard-Recht nicht abfragen –
  // vorher alle 4 s ein 403 (Live: 561x am Tag für einen Moderator).
  const cu = userStore.currentUser;
  const allowed = can(cu, "can_use_ai") && can(cu, "can_view_ai_dashboard");

  useEffect(() => {
    if (!serverId || !allowed) return;
    void aiStore.loadRuns();
    const iv = setInterval(() => void aiStore.loadRuns(), 4000);
    return () => clearInterval(iv);
  }, [serverId, allowed]);

  if (!serverId || !allowed) return null;

  const runs = aiStore.runs.filter((r) => r.serverId === serverId);
  if (runs.length === 0) return null;

  const newest = (arr: typeof runs) =>
    arr.reduce((a, b) =>
      new Date(a.createdAt).getTime() >= new Date(b.createdAt).getTime() ? a : b
    );

  const investigating = runs.filter((r) => r.status === "investigating");
  const open = runs.filter(
    (r) => r.status !== "investigating" && !r.patched
  );

  const goToReport = (runId: string) => {
    seen.current.add(runId);
    navigate(
      `/ai-dashboard?server=${encodeURIComponent(serverId)}&run=${encodeURIComponent(runId)}`
    );
  };

  // 1) Running -> progress bar.
  if (investigating.length > 0) {
    const run = newest(investigating);
    return (
      <div className="rounded-2xl border border-ember/40 bg-ember/[0.06] p-4">
        <div className="mb-2 flex items-center gap-2">
          <Loader2 size={15} className="animate-spin text-ember" />
          <span className="text-sm font-medium text-fg">KI-Diagnose läuft…</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-ember/15">
          <div className="gm-progress-bar h-full w-1/3 rounded-full bg-ember" />
        </div>
        <button
          onClick={() => goToReport(run.id)}
          className="mt-2 inline-flex items-center gap-1 text-xs text-ember hover:text-ember-2"
        >
          Aktuellen Bericht öffnen <ArrowRight size={13} />
        </button>
      </div>
    );
  }

  // 2) Done, not patched -> one-time hint + link to report.
  if (open.length > 0) {
    const run = newest(open);
    const isNew = !seen.current.has(run.id);
    const isFail = run.status === "failed";
    const label =
      run.status === "diagnosed"
        ? "Diagnose abgeschlossen"
        : run.status === "no_issue"
          ? "Kein Problem gefunden"
          : "Diagnose fehlgeschlagen";
    return (
      <div
        className={`rounded-2xl border p-4 ${
          isNew ? "border-ember/50 bg-ember/10" : "border-line bg-surface"
        }`}
      >
        <div className="flex items-center gap-2">
          {isFail ? (
            <AlertTriangle size={15} className="text-red-300" />
          ) : (
            <CheckCircle2 size={15} className="text-emerald-300" />
          )}
          <span className="text-sm font-medium text-fg">{label}</span>
          {isNew && (
            <span className="rounded bg-ember/20 px-1.5 py-0.5 text-[10px] font-medium text-ember">
              Neu
            </span>
          )}
        </div>
        <button
          onClick={() => goToReport(run.id)}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-ember hover:text-ember-2"
        >
          Bericht öffnen <ArrowRight size={13} />
        </button>
      </div>
    );
  }

  // 3) All runs patched -> nothing.
  return null;
});

